import type { CSSProperties, ReactNode } from "react";
import { C, tabular } from "../theme";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** l = left, c = center (default), r = right + tabular numbers. */
  align?: "l" | "c" | "r";
  /** Highlighted first-column style (white, semibold). */
  strong?: boolean;
  cellStyle?: (row: T) => CSSProperties | undefined;
  headerStyle?: CSSProperties;
}

const ALIGN = { l: "left", c: "center", r: "right" } as const;

export function DataTable<T>({
  columns,
  rows,
  caption,
  rowKey,
}: {
  columns: Column<T>[];
  rows: T[];
  /** Visually hidden table caption for screen readers. */
  caption: string;
  rowKey?: (row: T, i: number) => string | number;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
        }}
      >
        <caption
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th
                key={c.key}
                scope="col"
                style={{
                  fontWeight: 700,
                  color: C.white,
                  textAlign: ALIGN[c.align ?? "c"],
                  padding: "10px 12px",
                  whiteSpace: "nowrap",
                  borderBottom: `1px solid ${C.lineStrong}`,
                  borderLeft: i > 0 ? `1px solid ${C.lineSoft}` : undefined,
                  ...c.headerStyle,
                }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={rowKey ? rowKey(row, ri) : ri}>
              {columns.map((c, i) => (
                <td
                  key={c.key}
                  style={{
                    color: c.strong ? C.white : C.text,
                    fontWeight: c.strong ? 600 : undefined,
                    textAlign: ALIGN[c.align ?? "c"],
                    padding: "8px 12px",
                    whiteSpace: "nowrap",
                    borderBottom:
                      ri < rows.length - 1
                        ? `1px solid ${C.lineSoft}`
                        : undefined,
                    borderLeft: i > 0 ? `1px solid ${C.lineSoft}` : undefined,
                    ...(c.align === "r" ? tabular : undefined),
                    ...c.cellStyle?.(row),
                  }}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
